// fullscreen.ts — a "material" that runs a fragment shader over the whole
// screen via a single oversized triangle (no vertex buffers, no geometry).
//
// An effect shader only has to define:
//
//   fn render(uv: vec2<f32>, frag: vec2<f32>) -> vec3<f32> { ... }
//
// We staple on the Globals struct, the bind group, the fullscreen-triangle
// vertex stage, and the fragment entry point. That convention is the whole
// "engine" — everything creative lives in `render`.

import type { GPU } from "./gpu";
import { Globals } from "./uniforms";

const PRELUDE = /* wgsl */ `
${Globals.WGSL}

@group(0) @binding(0) var<uniform> G : Globals;

struct VSOut {
  @builtin(position) clip : vec4<f32>,
  @location(0)       uv   : vec2<f32>,
};

@vertex
fn vs(@builtin(vertex_index) i : u32) -> VSOut {
  // One triangle that covers the clip volume; uv in 0..1 across the screen.
  var p = array<vec2<f32>, 3>(
    vec2<f32>(-1.0, -1.0),
    vec2<f32>( 3.0, -1.0),
    vec2<f32>(-1.0,  3.0),
  );
  let pos = p[i];
  var out : VSOut;
  out.clip = vec4<f32>(pos, 0.0, 1.0);
  out.uv = vec2<f32>(pos.x * 0.5 + 0.5, 1.0 - (pos.y * 0.5 + 0.5));
  return out;
}
`;

const EPILOGUE = /* wgsl */ `
@fragment
fn fs(in : VSOut) -> @location(0) vec4<f32> {
  let frag = in.uv * G.resolution;
  let rgb = render(in.uv, frag);
  return vec4<f32>(rgb, 1.0);
}
`;

export interface FullscreenMaterialDesc {
  readonly label: string;
  /** WGSL containing `fn render(uv: vec2<f32>, frag: vec2<f32>) -> vec3<f32>`. */
  readonly source: string;
}

export class FullscreenMaterial {
  private readonly pipeline: GPURenderPipeline;
  private readonly bindGroup: GPUBindGroup;

  constructor(gpu: GPU, globals: Globals, desc: FullscreenMaterialDesc) {
    const module = gpu.device.createShaderModule({
      label: `${desc.label} shader`,
      code: `${PRELUDE}\n${desc.source}\n${EPILOGUE}`,
    });

    this.pipeline = gpu.device.createRenderPipeline({
      label: desc.label,
      layout: "auto",
      vertex: { module, entryPoint: "vs" },
      fragment: {
        module,
        entryPoint: "fs",
        targets: [{ format: gpu.format }],
      },
      primitive: { topology: "triangle-list" },
    });

    this.bindGroup = gpu.device.createBindGroup({
      label: `${desc.label} bind group`,
      layout: this.pipeline.getBindGroupLayout(0),
      entries: [{ binding: 0, resource: { buffer: globals.buffer } }],
    });
  }

  draw(pass: GPURenderPassEncoder): void {
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    pass.draw(3);
  }
}
